import React, { useEffect, useState } from 'react';
import axios from 'axios';
import RoomEditModal from './RoomEditModal';
import RoomDetailsModal from './RoomDetailsModal';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE } from '../../config/apiBase';

const API = `${API_BASE}/manager/website`;

export default function RoomsTab() {
  const { token } = useAuth();

  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState('');
  const [counts, setCounts] = useState({});
  const [editRoom, setEditRoom] = useState(null);
  const [selectedRoomDetails, setSelectedRoomDetails] = useState(null);
  const [totalRoomsAllowed, setTotalRoomsAllowed] = useState(null);
  const [collapsedFloors, setCollapsedFloors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFloor, setFilterFloor] = useState('all');
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  const getRoomFloorId = (room) => Number(room.hotel_floor_id ?? room.floor_id ?? room.floor);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const toggleFloor = (floorId) => {
    setCollapsedFloors(prev => ({
      ...prev,
      [floorId]: !prev[floorId]
    }));
  };

  const syncWithSmartThings = async () => {
    setSyncing(true);
    setSyncStatus(null);
    try {
      const res = await axios.post(`${API}/rooms/sync-smartthings`, {}, auth);
      setSyncStatus({
        type: 'success',
        message: `Sync completed: ${res.data.data.synced} synced, ${res.data.data.created} created`
      });
      // Refresh rooms to show updated sync status
      fetchRooms();
    } catch (err) {
      console.error('SmartThings sync error:', err);
      setSyncStatus({
        type: 'error',
        message: err.response?.data?.message || 'Failed to sync with SmartThings'
      });
    } finally {
      setSyncing(false);
    }
  };

  const fetchTotalRoomsAllowed = async () => {
    // Fetch hotel info to get total_rooms
    try {
      const res = await axios.get(`${API}/hotel`, auth);
      if (res.data?.data?.total_rooms) {
        setTotalRoomsAllowed(Number(res.data.data.total_rooms));
      }
    } catch (err) {
      setTotalRoomsAllowed(null);
    }
  };

  useEffect(() => {
    fetchFloors();
    fetchRooms();
    fetchRoomTypes();
    fetchTotalRoomsAllowed();
  }, []);

  const fetchFloors = async () => {
    const res = await axios.get(`${API}/floors`, auth);
    setFloors(res.data.data);
  };

  const fetchRooms = async () => {
    const res = await axios.get(`${API}/rooms`, auth);
    setRooms(res.data.data);
  };

  const fetchRoomTypes = async () => {
    const res = await axios.get(`${API}/room-types`, auth);
    setRoomTypes(res.data.data);
  };

  const bulkCreateRooms = async () => {
    if (!selectedFloor) return alert('Select floor');

    const totalToAdd = Object.values(counts).reduce((sum, val) => sum + Number(val || 0), 0);
    const totalExistingRooms = rooms.length;
    if (totalRoomsAllowed !== null && (totalExistingRooms + totalToAdd > totalRoomsAllowed)) {
      return alert(`Cannot add more than ${totalRoomsAllowed} rooms in total.`);
    }
    if (totalToAdd === 0) {
      return alert('Please enter the number of rooms to add.');
    }

    const payload = {
      hotel_floor_id: selectedFloor,
      rooms: Object.entries(counts)
        .filter(([_, count]) => Number(count) > 0)
        .map(([room_type_id, count]) => ({
          room_type_id: Number(room_type_id),
          count: Number(count)
        }))
    };
    console.log('BULK ROOMS PAYLOAD:', payload);
    try {
      await axios.post(`${API}/rooms/bulk`, payload, auth);
      setCounts({});
      fetchRooms();
    } catch (err) {
      console.error('BULK ROOMS ERROR:', err.response?.data || err);
      alert(err.response?.data?.message || 'Failed to add rooms');
    }
  };

  const deleteRoom = async (id) => {
    if (!window.confirm('Delete this room?')) return;
    await axios.delete(`${API}/rooms/${id}`, auth);
    fetchRooms();
  };

  // Calculate total rooms to add and total existing rooms
  const totalToAdd = Object.values(counts).reduce((sum, val) => sum + Number(val || 0), 0);
  const totalExistingRooms = rooms.length;
  const disableAddRooms =
    totalRoomsAllowed !== null && (totalExistingRooms + totalToAdd > totalRoomsAllowed || totalToAdd === 0);

  // Tooltip logic for Add Rooms button
  let addRoomsTooltip = '';
  if (totalRoomsAllowed === null) {
    addRoomsTooltip = 'Loading room limit...';
  } else if (totalToAdd === 0) {
    addRoomsTooltip = 'Enter the number of rooms to add.';
  } else if (totalExistingRooms + totalToAdd > totalRoomsAllowed) {
    addRoomsTooltip = `Cannot add more than ${totalRoomsAllowed} rooms in total.`;
  } else {
    addRoomsTooltip = `You can add up to ${totalRoomsAllowed - totalExistingRooms} more rooms.`;
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 16px', fontFamily: 'Segoe UI, Arial, sans-serif' }}>
      <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.08)', padding: 32, marginBottom: 32 }}>
        <h2 style={{ marginBottom: 24, color: '#2d3748', fontWeight: 700 }}>Bulk Create Rooms</h2>
        <div style={{ display: 'flex', gap: 24, alignItems: 'center', marginBottom: 24, position: 'relative' }}>
          <div>
            <label style={{ fontWeight: 500, color: '#4a5568' }}>Select Floor</label><br />
            <select onChange={e => setSelectedFloor(e.target.value)} value={selectedFloor} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e0', minWidth: 120 }}>
              <option value="">Select Floor</option>
              {floors.map(f => (
                <option key={f.id} value={f.id}>Floor {f.floor_id}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            {roomTypes.map(rt => (
              <div key={rt.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <label style={{ color: '#4a5568', fontWeight: 500 }}>{rt.room_type}</label>
                <input
                  type="number"
                  min="0"
                  value={counts[rt.id] || ''}
                  onChange={e => setCounts({ ...counts, [rt.id]: e.target.value })}
                  style={{ width: 90, padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e0', marginTop: 4, textAlign: 'center', fontSize: 16 }}
                  placeholder="Count"
                />
              </div>
            ))}
          </div>
          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {disableAddRooms && (
              <div style={{
                position: 'absolute',
                bottom: '110%',
                left: '50%',
                transform: 'translateX(-50%)',
                background: '#2d3748',
                color: '#fff',
                padding: '7px 16px',
                borderRadius: 8,
                fontSize: 13,
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                zIndex: 10,
                marginBottom: 6,
                pointerEvents: 'none',
                opacity: 0.95,
              }}>
                {addRoomsTooltip}
              </div>
            )}
            <button
              onClick={bulkCreateRooms}
              disabled={disableAddRooms}
              style={{
                background: '#123049FF',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '8px 12px',
                fontWeight: 600,
                fontSize: 14,
                cursor: disableAddRooms ? 'not-allowed' : 'pointer',
                opacity: disableAddRooms ? 0.6 : 1,
              }}
            >
              ➕ Add Rooms
            </button>
          </div>
        </div>
      </div>
      
      <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.08)', padding: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <h2 style={{ margin: 0, color: '#2d3748', fontWeight: 700 }}>Existing Rooms ({rooms.length})</h2>
            <button
              onClick={syncWithSmartThings}
              disabled={syncing}
              style={{
                background: '#3182ce',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 14,
                fontWeight: 600,
                cursor: syncing ? 'not-allowed' : 'pointer',
                opacity: syncing ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'background 0.2s'
              }}
              onMouseEnter={e => !syncing && (e.currentTarget.style.background = '#2c5aa0')}
              onMouseLeave={e => !syncing && (e.currentTarget.style.background = '#3182ce')}
              title="Sync rooms with SmartThings IoT platform"
            >
              {syncing ? (
                <>
                  <span style={{ fontSize: 16 }}>🔄</span>
                  Syncing...
                </>
              ) : (
                <>
                  <span style={{ fontSize: 16 }}>🔗</span>
                  Sync SmartThings
                </>
              )}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="🔍 Search rooms..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ 
                padding: '8px 12px', 
                borderRadius: 8, 
                border: '1px solid #cbd5e0',
                fontSize: 14,
                minWidth: 200
              }}
            />
            <select 
              value={filterFloor} 
              onChange={e => setFilterFloor(e.target.value)}
              style={{ 
                padding: '8px 12px', 
                borderRadius: 8, 
                border: '1px solid #cbd5e0',
                fontSize: 14
              }}
            >
              <option value="all">All Floors</option>
              {floors.map(f => (
                <option key={f.id} value={f.id}>Floor {f.floor_id}</option>
              ))}
            </select>
          </div>
        </div>
        
        {syncStatus && (
          <div style={{
            padding: '12px 16px',
            borderRadius: 8,
            marginBottom: 16,
            background: syncStatus.type === 'success' ? '#d4edda' : '#f8d7da',
            border: `1px solid ${syncStatus.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
            color: syncStatus.type === 'success' ? '#155724' : '#721c24',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <span style={{ fontSize: 18 }}>{syncStatus.type === 'success' ? '✅' : '❌'}</span>
            <span>{syncStatus.message}</span>
          </div>
        )}
        
        {floors.length === 0 ? (
          <div style={{ color: '#a0aec0', fontSize: 18, textAlign: 'center', margin: '32px 0' }}>No floors found for this hotel.</div>
        ) : (
          floors
            .filter(floor => filterFloor === 'all' || floor.id === Number(filterFloor))
            .map(floor => {
            const roomsOnFloor = rooms
              .filter(r => getRoomFloorId(r) === Number(floor.id))
              .filter(r => 
                searchTerm === '' || 
                r.room_number.toString().includes(searchTerm) ||
                r.room_type.toLowerCase().includes(searchTerm.toLowerCase())
              );
            
            const isCollapsed = collapsedFloors[floor.id];
            const totalRoomsOnFloor = rooms.filter(r => getRoomFloorId(r) === Number(floor.id)).length;
            
            if (totalRoomsOnFloor === 0 && filterFloor === 'all') {
              return null; // Don't show empty floors in "all" view
            }

            // Group rooms by room_type
            const grouped = {};
            roomsOnFloor.forEach(r => {
              if (!grouped[r.room_type]) grouped[r.room_type] = [];
              grouped[r.room_type].push(r);
            });

            return (
              <div key={floor.id} style={{ 
                marginBottom: 24,
                border: '2px solid #e2e8f0',
                borderRadius: 12,
                overflow: 'hidden',
                background: '#fafafa'
              }}>
                <div 
                  onClick={() => toggleFloor(floor.id)}
                  style={{
                    color: '#123049',
                    fontWeight: 800,
                    fontSize: '1.5rem',
                    letterSpacing: '0.04em',
                    padding: '16px 24px',
                    background: 'linear-gradient(90deg, #e3eafc 60%, #f7fafc 100%)',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'background 0.2s',
                    userSelect: 'none'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'linear-gradient(90deg, #d4e2f7 60%, #edf2f7 100%)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'linear-gradient(90deg, #e3eafc 60%, #f7fafc 100%)'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: '1.8rem', transition: 'transform 0.2s', display: 'inline-block', transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>
                      ▼
                    </span>
                    <span>Floor {floor.floor_id}</span>
                    <span style={{ 
                      fontSize: '0.9rem', 
                      fontWeight: 600, 
                      color: '#4a5568',
                      background: '#fff',
                      padding: '4px 12px',
                      borderRadius: 20,
                      border: '1px solid #cbd5e0'
                    }}>
                      {totalRoomsOnFloor} {totalRoomsOnFloor === 1 ? 'room' : 'rooms'}
                    </span>
                  </div>
                  <span style={{ fontSize: '0.85rem', color: '#718096', fontWeight: 500 }}>
                    {isCollapsed ? 'Click to expand' : 'Click to collapse'}
                  </span>
                </div>
                
                {!isCollapsed && (
                  <div style={{ padding: '24px' }}>
                    {roomsOnFloor.length === 0 && searchTerm && (
                      <div style={{ color: '#a0aec0', fontSize: 16, textAlign: 'center', padding: '32px 0' }}>
                        No rooms match your search on this floor.
                      </div>
                    )}
                    {Object.keys(grouped).map(roomType => (
                      <div key={roomType} style={{ marginBottom: 18 }}>
                        <div style={{
                          fontWeight: 700,
                          color: '#0a2540',
                          marginBottom: 10,
                          fontSize: '1.35rem',
                          letterSpacing: '0.03em',
                          textShadow: '0 1px 6px rgba(42,100,176,0.10)',
                          borderLeft: '5px solid #3182ce',
                          paddingLeft: 12,
                          background: 'linear-gradient(90deg, #e6f0fa 60%, #fff 100%)',
                          borderRadius: 6,
                          display: 'inline-block',
                          boxShadow: '0 1px 6px rgba(49,130,206,0.07)'
                        }}>{roomType}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px' }}>
                          {grouped[roomType]
                            .sort((a, b) => a.room_number - b.room_number)
                            .map(r => (
                              <div
                                key={r.id}
                                style={{
                                  minWidth: 180,
                                  maxWidth: 220,
                                  padding: '16px 18px',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '16px',
                                  background: '#f7fafc',
                                  boxShadow: '0 1px 6px rgba(160,174,192,0.08)',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  position: 'relative',
                                  transition: 'box-shadow 0.2s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(49,130,206,0.15)'}
                                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 6px rgba(160,174,192,0.08)'}
                              >
                                <div style={{ fontSize: 22, fontWeight: 700, color: '#2b6cb0', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                                  Room {r.room_number}
                                  {r.sync_status === 'synced' && (
                                    <span 
                                      title={`Synced with SmartThings\nRoom ID: ${r.smartthings_room_id}\nLast synced: ${r.last_synced ? new Date(r.last_synced).toLocaleString() : 'N/A'}`}
                                      style={{ 
                                        fontSize: 12, 
                                        background: '#48bb78', 
                                        color: '#fff',
                                        padding: '2px 8px',
                                        borderRadius: 12,
                                        fontWeight: 600
                                      }}
                                    >
                                      🔗 IoT
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontSize: 15, color: '#4a5568', marginBottom: 8 }}>
                                  <span style={{ fontWeight: 500 }}>Type:</span> {r.room_type}
                                </div>
                                <div style={{ fontSize: 14, color: '#718096', marginBottom: 8 }}>
                                  <span style={{ fontWeight: 500 }}>Status:</span> {r.status}
                                </div>
                                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                                  <button
                                    onClick={() => setSelectedRoomDetails(r)}
                                    style={{
                                      background: '#e6fffa',
                                      border: '1px solid #319795',
                                      color: '#319795',
                                      borderRadius: 6,
                                      padding: '4px 10px',
                                      fontSize: 13,
                                      fontWeight: 500,
                                      cursor: 'pointer',
                                      transition: 'background 0.2s',
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                    title="View Details & Manage Devices"
                                    onMouseEnter={e => e.currentTarget.style.background = '#b2f5ea'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#e6fffa'}
                                  >
                                    <span role="img" aria-label="view" style={{ fontSize: 15, marginRight: 2 }}>👁️</span>
                                  </button>
                                  <button
                                    onClick={() => setEditRoom(r)}
                                    style={{
                                      background: '#ebf8ff',
                                      border: '1px solid #3182ce',
                                      color: '#3182ce',
                                      borderRadius: 6,
                                      padding: '4px 10px',
                                      fontSize: 13,
                                      fontWeight: 500,
                                      cursor: 'pointer',
                                      transition: 'background 0.2s',
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                    title="Edit Room"
                                    onMouseEnter={e => e.currentTarget.style.background = '#bee3f8'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#ebf8ff'}
                                  >
                                    <span role="img" aria-label="edit" style={{ fontSize: 15, marginRight: 2 }}>✏️</span>
                                  </button>
                                  <button
                                    onClick={() => deleteRoom(r.id)}
                                    style={{
                                      background: '#fff5f5',
                                      border: '1px solid #e53e3e',
                                      color: '#e53e3e',
                                      borderRadius: 6,
                                      padding: '4px 10px',
                                      fontSize: 13,
                                      fontWeight: 500,
                                      cursor: 'pointer',
                                      transition: 'background 0.2s',
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                    title="Delete Room"
                                    onMouseEnter={e => e.currentTarget.style.background = '#fed7d7'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#fff5f5'}
                                  >
                                    <span role="img" aria-label="delete" style={{ fontSize: 15, marginRight: 2 }}>🗑️</span>
                                  </button>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
        
        {rooms.length > 0 && floors.filter(floor => filterFloor === 'all' || floor.id === Number(filterFloor)).every(floor => 
          rooms.filter(r => getRoomFloorId(r) === Number(floor.id)).filter(r => 
            searchTerm === '' || 
            r.room_number.toString().includes(searchTerm) ||
            r.room_type.toLowerCase().includes(searchTerm.toLowerCase())
          ).length === 0
        ) && (
          <div style={{ color: '#a0aec0', fontSize: 18, textAlign: 'center', margin: '32px 0' }}>
            No rooms match your search criteria.
          </div>
        )}
      </div>

      {editRoom && (
        <RoomEditModal
          room={editRoom}
          roomTypes={roomTypes}
          onClose={() => setEditRoom(null)}
          onSaved={fetchRooms}
        />
      )}

      {selectedRoomDetails && (
        <RoomDetailsModal
          room={selectedRoomDetails}
          onClose={() => setSelectedRoomDetails(null)}
          onUpdate={fetchRooms}
          token={token}
        />
      )}
    </div>
  );
}
